import { MENU_ITEMS, MenuItemTypes } from "./menu";

// =============================================================================
// Menu → sections: the grouping rules, as a pure function
// -----------------------------------------------------------------------------
// Extracted from portalNav.buildAdminNav so the SHAPE of the sidebar (which
// sections exist, which items land in them, who may see them) lives in exactly
// one place. portalNav then only adds presentation (icon mapping); the e2e
// suite imports this directly, so nav tests can never drift from the nav.
//
// Deliberately free of React, router and asset imports — it must be importable
// from a plain Node/Playwright process, which is why it is a sibling of
// menu.ts rather than part of portalNav.ts.
//
// Rules (unchanged from the original implementation):
//   1. an `isTitle` marker opens a section; following leaf items belong to it
//   2. a parent-with-children is its own section, and drops a preceding title
//      that collected no items (so "Fleet & Inventory" appears once, not twice)
//   3. an item is visible if it declares no `permissions`, or the user holds
//      one of them
// =============================================================================

export interface MenuSection {
  /** Stable key: the title for title-sections, the parent item's key otherwise. */
  key: string;
  title: string;
  /**
   * Raw (legacy Feather) icon name of the parent item, for parent-sections
   * only — children inherit it unless they override. Undefined for
   * title-sections, whose leaves carry their own icons.
   */
  icon?: string;
  items: MenuItemTypes[];
}

export const isMenuItemVisible = (
  item: { permissions?: string[] },
  permissions: string[]
): boolean =>
  !item.permissions ||
  item.permissions.length === 0 ||
  item.permissions.some((p) => permissions.includes(p));

/** Sections + items a user holding `permissions` should see, in menu order. */
export const buildMenuSections = (
  permissions: string[],
  menu: MenuItemTypes[] = MENU_ITEMS
): MenuSection[] => {
  const sections: MenuSection[] = [];

  let titleLabel: string | null = null;
  let pending: MenuItemTypes[] = [];

  const flush = () => {
    if (titleLabel && pending.length) {
      sections.push({ key: titleLabel, title: titleLabel, items: pending });
    }
    pending = [];
  };

  for (const item of menu) {
    if (item.isTitle) {
      flush();
      titleLabel = item.label;
      continue;
    }

    if (item.children && item.children.length) {
      flush();
      titleLabel = null;
      if (!isMenuItemVisible(item, permissions)) continue;
      const children = item.children.filter((c) => isMenuItemVisible(c, permissions));
      if (children.length) {
        sections.push({ key: item.key, title: item.label, icon: item.icon, items: children });
      }
      continue;
    }

    if (!isMenuItemVisible(item, permissions)) continue;
    pending.push(item);
  }
  flush();

  return sections;
};

/**
 * Every permission any menu entry gates on — i.e. the set that makes the whole
 * menu visible. Used by the e2e suite to report how much of the menu a test
 * account cannot see (silent coverage loss otherwise).
 */
export const allMenuPermissions = (menu: MenuItemTypes[] = MENU_ITEMS): string[] => {
  const perms = new Set<string>();
  const visit = (items: MenuItemTypes[]) => {
    for (const item of items) {
      item.permissions?.forEach((p) => perms.add(p));
      if (item.children) visit(item.children);
    }
  };
  visit(menu);
  return Array.from(perms);
};
